import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import BioentityVersionChip from 'molbio/BioentityVersionChip';
import {VisualHistoryEventMetadataFieldFragment} from 'molbio/visual-history/__generated/VisualHistoryEventMetadataFieldFragment';
import {FONT_PROXIMA_12, FONT_PROXIMA_13} from 'styles/FontConstants';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {GRAY_DARKER} from 'ui-library/Colors';

type VisualHistoryMetadataFieldValueNode = VisualHistoryEventMetadataFieldFragment['value'];

export const VISUAL_HISTORY_EVENT_METADATA_FIELD_FRAGMENT = gql`
  fragment VisualHistoryEventMetadataFieldFragment on VisualHistoryEventMetadataFieldNode {
    name
    value {
      ... on VisualHistoryEventMetadataFieldTextValueNode {
        text
      }
      ... on EntityIdNode {
        entityId
      }
      ... on EntityVersionIdNode {
        entityId
        entityVersionId
        entityType
      }
      ... on VisualHistorySequenceAlignmentIdNode {
        alignmentId
        sequenceId
      }
    }
  }
`;

export function VisualHistoryEventMetadataFieldValue({
  value,
}: {
  value: VisualHistoryMetadataFieldValueNode;
}): JSX.Element {
  if (value.__typename === 'VisualHistoryEventMetadataFieldTextValueNode') {
    return <p className={css(styles.textValue)}>{value.text}</p>;
  } else if (value.__typename === 'EntityIdNode') {
    return <BenchChip id={value.entityId} size={ChipSize.MINI} />;
  } else if (value.__typename === 'EntityVersionIdNode') {
    return (
      <BioentityVersionChip
        id={value.entityId}
        versionId={Number.parseInt(value.entityVersionId, 10)}
        size={ChipSize.MINI}
      />
    );
  } else {
    // TODO: Link to an alignment
    return <div />;
  }
}

export interface VisualHistoryEventMetadataFieldProps {
  name: string;
  value: VisualHistoryMetadataFieldValueNode;
}

/**
 * Component to render a visual history field name and value.
 * @param field A visual history name and value pair.
 * @constructor
 */
export default function VisualHistoryEventMetadataField({
  name,
  value,
}: VisualHistoryEventMetadataFieldProps): JSX.Element {
  return (
    <div>
      {name.length > 0 && <p className={css(styles.name)}>{name}</p>}
      <VisualHistoryEventMetadataFieldValue value={value} />
    </div>
  );
}

const styles = StyleSheet.create({
  name: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    marginBottom: '4px',
  },
  textValue: {
    ...FONT_PROXIMA_12,
    marginBottom: 0,
  },
});
